import React, {useCallback, useEffect, useState} from 'react';
import TechnicalComponentsContent from './TechnicalComponentsContent';
import DocumentationContent from './DocumentationContent';
import PhotosContent from './PhotosContent';
import {UNIT_STATUS_OPTIONS} from "./support/UnitStatus";
import {UnitTitle} from "./component/primaryinfo/UnitTitle";
import {UnitCustomIdInput} from "./component/primaryinfo/UnitIdInput";
import {UnitNameInput} from "./component/primaryinfo/UnitNameInput";
import {UnitStatus} from "./component/primaryinfo/UnitStatus";
import axios from "axios";
import { API_ROOT as API } from '../../config';
import {validateUnit} from "./support/UnitValidator";
import {filterErrorForField} from "../../utils/ErrorUtils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from "../common/ConfirmModal";

const UnitComponent = ({ unit, building, onDelete }) => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem(`unit-tab-${unit.id}`);
    return savedTab || 'technical';
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [customUnitId, setCustomUnitId] = useState(unit.custom_unit_id || '');
  const [name, setName] = useState(unit.name || '');
  const [unitStatus, setUnitStatus] = useState(unit.status || UNIT_STATUS_OPTIONS.UNKNOWN.value);
  const [technicalData, setTechnicalData] = useState(unit.technical_components || {});
  const [validationErrors, setValidationErrors] = useState([]);
  const [unitDocuments, setUnitDocuments] = useState(unit?.documents || []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem(`unit-tab-${unit.id}`, tab);
  };

  const currentStatus = UNIT_STATUS_OPTIONS[unitStatus] || UNIT_STATUS_OPTIONS['UNKNOWN'];

  const updateTechnicalComponent = useCallback(
      (componentPath, value) => {
        let newTechnicalData = {...technicalData};

        // Update the specific field using dot notation
        const pathParts = componentPath.split('.');
        let current = newTechnicalData;

        // Navigate to the parent object
        for (let i = 0; i < pathParts.length - 1; i++) {
          if (!current[pathParts[i]]) {
            current[pathParts[i]] = {};
          }
          current = current[pathParts[i]];
        }

        // Set the final value
        current[pathParts[pathParts.length - 1]] = value;

        setTechnicalData(newTechnicalData);

      }, [technicalData]);

  const handleSave = useCallback(async () => {
    if (validationErrors.length > 0) {
      return;
    }
    setLoading(true);

    try {
      const updateData = {
        externalId: unit.id,
        floorExternalId: unit.floor_id,
        name: name,
        type: unit.unit_type,
        number: unit.unit_number,
        externalUniqueId: customUnitId,
        status: unitStatus,
        portGfGv: technicalData.port_gf_gv,
        portGfAp: technicalData.port_gf_ap,
        macAddressOntSerialNumber: technicalData.ont?.serial_number,
        macAddressOnt: technicalData.ont?.mac_address,
        macAddressCpe: technicalData.cpe?.mac_address,
        homeIdentifier: technicalData.home_id?.value,
        documents: unitDocuments,
        version: unit.version
      };

      const result = await saveToBackend(updateData);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      } else {
        setErrors({general: result.error});
      }
    } catch (error) {
      setErrors({general: 'Ein unerwarteter Fehler ist aufgetreten'});
    } finally {
      setLoading(false);
    }
  }, [name, customUnitId, unitStatus,
    technicalData, unitDocuments, validationErrors
  ]);

  const saveToBackend = async (unit) => {
    console.log(`Save to BE ${JSON.stringify(unit)}`);
    try {
      await axios.put(`${API}/units`, unit);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Einheit-Update fehlgeschlagen';
      return { success: false, error: errorMessage };
    }
  };

  const handleDelete = useCallback(async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/units/${unit.id}`);
      if (onDelete) onDelete(unit.id);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Einheit konnte nicht gelöscht werden';
      setErrors({ general: errorMessage });
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  }, [unit.id, onDelete]);

  useEffect(() => {
    const validationErrorsList = validateUnit(name, unitStatus,
        technicalData, unitDocuments);
    setValidationErrors(validationErrorsList);
  }, [name, unitStatus, technicalData, unitDocuments]);

  return (
    <div className="border border-gray-200 rounded-lg">
      {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <p className="text-sm text-red-600">{errors.general}</p>
          </div>
      )}

      {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
            <p className="text-sm text-green-600">Einheit erfolgreich aktualisiert!</p>
          </div>
      )}

      <div className="px-3 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <UnitTitle customUnitId={customUnitId} unit={unit}/>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="ml-2 text-gray-400 hover:text-red-500 transition-colors p-1"
            title="Einheit löschen"
          >
            <FontAwesomeIcon icon={faTrash} className="text-sm" />
          </button>
        </div>

        <div className="flex space-x-4">
          <UnitCustomIdInput
              customUnitId={customUnitId}
              displayName={unit.display_name}
              onChange={setCustomUnitId}
          />

          <UnitNameInput name={name} onChange={setName} errors={filterErrorForField(validationErrors, 'name')}/>
        </div>
      </div>

      {/* Unit Status Section */}
      <div className="px-3 py-3 bg-white border-b border-gray-200">
        <UnitStatus currentStatus={currentStatus} unitStatus={unitStatus} onChange={setUnitStatus} errors={filterErrorForField(validationErrors, 'status')}/>
      </div>

      {/* Individual Unit Tab Navigation */}
      <div className="border-b border-gray-200 px-1 sm:px-3">
        <nav className="-mb-px flex space-x-1 sm:space-x-4">
          <button
            onClick={(e) => {
              e.preventDefault();
              handleTabChange('technical');
            }}
            onTouchStart={(e) => e.preventDefault()}
            className={`py-2 px-1 sm:px-2 border-b-2 font-medium text-xs flex-1 min-w-0 truncate ${
              activeTab === 'technical'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="hidden sm:inline">Technische Komponenten</span>
            <span className="sm:hidden">Technik</span>
          </button>
          <button
            disabled={false}
            onClick={(e) => {
              e.preventDefault();
              handleTabChange('documentation');
            }}
            onTouchStart={(e) => e.preventDefault()}
            className={`py-2 px-1 sm:px-2 border-b-2 font-medium text-xs flex-1 min-w-0 truncate ${
              activeTab === 'documentation'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="hidden sm:inline">Dokumentation</span>
            <span className="sm:hidden">Docs</span>
          </button>
          <button
            disabled={false}
            onClick={(e) => {
              e.preventDefault();
              handleTabChange('fotos');
            }}
            onTouchStart={(e) => e.preventDefault()}
            className={`py-2 px-1 sm:px-2 border-b-2 font-medium text-xs flex-1 min-w-0 truncate ${
              activeTab === 'fotos'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fotos
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-3" style={{ backgroundColor: '#F6F6F6' }}>
        {activeTab === 'technical' && (
          <TechnicalComponentsContent
            unitId={unit.id}
            technicalData={technicalData}
            updateUnitTechnicalComponent={updateTechnicalComponent}
            errors={filterErrorForField(validationErrors, 'technicalData')}
          />
        )}

        {activeTab === 'documentation' && (
          <DocumentationContent
            unit={unit}
            building={building}
            unitDocuments={unitDocuments}
            setUnitDocuments={setUnitDocuments}
          />
        )}

        {activeTab === 'fotos' && (
          <PhotosContent
            building={building}
            unitDocuments={unitDocuments}
            setUnitDocuments={setUnitDocuments}
          />
        )}
      </div>

      <div className={"px-3 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2"}>
        <button
            key={unit.id}
            type="button"
            disabled={loading || validationErrors?.length > 0}
            className={`flex-1 btn-primary ${loading || validationErrors?.length > 0 ? 'opacity-50 bg-gray-400 border-gray-400 cursor-not-allowed' : ''}`}
            onClick={handleSave}>
          {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Speichern...
              </div>
          ) : (
              'Speichern'
          )}
        </button>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmModal
          text="Diese Einheit wird unwiderruflich gelöscht. Möchten Sie fortfahren?"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmLoading={deleteLoading}
        />
      )}
    </div>
  );
};

export default UnitComponent;
